import API_BASE from '../../utils/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';

const PrincipalSubjects = () => {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ subjectName: '', teacherId: '' });
  const [editingSubject, setEditingSubject] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [allSubjects, setAllSubjects] = useState([]);
  const [viewMode, setViewMode] = useState('byClass'); // 'byClass' or 'overview'

  useEffect(() => {
    fetchClasses();
    fetchTeachers();
    fetchAllSubjects();
  }, []);

  useEffect(() => {
    if (selectedClass) fetchSubjects();
  }, [selectedClass]);

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API_BASE}/classes`);
      setClasses(res.data);
      if (res.data.length > 0) setSelectedClass(res.data[0]._id);
    } catch (err) {
      console.error('Error fetching classes:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/teachers`);
      setTeachers(res.data);
    } catch (err) {
      console.error('Error fetching teachers:', err);
    }
  };

  const fetchSubjects = async () => {
    try {
      const res = await axios.get(`${API_BASE}/subjects/class/${selectedClass}`);
      setSubjects(res.data);
    } catch (err) {
      console.error('Error fetching subjects:', err);
    }
  };

  const fetchAllSubjects = async () => {
    try {
      const res = await axios.get(`${API_BASE}/subjects/all`);
      setAllSubjects(res.data);
    } catch (err) {
      console.error('Error fetching all subjects:', err);
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (editingSubject) {
        await axios.put(
          `${API_BASE}/subjects/assign/${selectedClass}/${editingSubject._id}`,
          { teacherId: formData.teacherId }
        );
        setSuccess('Subject teacher updated');
      } else {
        await axios.post(`${API_BASE}/subjects/assign`, {
          classId: selectedClass,
          subjectName: formData.subjectName,
          teacherId: formData.teacherId || null
        });
        setSuccess('Subject assigned successfully');
      }
      fetchSubjects();
      fetchAllSubjects();
      setShowModal(false);
      setFormData({ subjectName: '', teacherId: '' });
      setEditingSubject(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign subject');
    }
  };

  const handleDelete = async (subjectId) => {
    if (!window.confirm('Remove this subject from the class?')) return;
    try {
      await axios.delete(`${API_BASE}/subjects/assign/${selectedClass}/${subjectId}`);
      fetchSubjects();
      fetchAllSubjects();
      setSuccess('Subject removed');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove subject');
    }
  };

  const openEdit = (subject) => {
    setEditingSubject(subject);
    setFormData({ subjectName: subject.subjectName, teacherId: subject.teacherId?._id || '' });
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Subject Management</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="p-6">
          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">{success}</div>
          )}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
          )}

          {/* View toggle */}
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setViewMode('byClass')}
              className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'byClass' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            >
              By Class
            </button>
            <button
              onClick={() => setViewMode('overview')}
              className={`px-4 py-2 rounded-lg font-medium ${viewMode === 'overview' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700'}`}
            >
              Overview
            </button>
          </div>

          {viewMode === 'byClass' ? (
            <>
              {/* Class selector + Add button */}
              <div className="bg-white rounded-lg shadow p-6 mb-6 flex items-end gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Class</label>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    {classes.map((cls) => (
                      <option key={cls._id} value={cls._id}>
                        {cls.className} - {cls.section}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => { setShowModal(true); setEditingSubject(null); setFormData({ subjectName: '', teacherId: '' }); }}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700"
                >
                  + Assign Subject
                </button>
              </div>

              {/* Subjects table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b">
                  <h2 className="text-lg font-semibold">Subjects ({subjects.length})</h2>
                </div>
                <div className="p-6">
                  {subjects.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Teacher</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {subjects.map((sub, idx) => (
                          <tr key={sub._id}>
                            <td className="px-6 py-4 whitespace-nowrap">{idx + 1}</td>
                            <td className="px-6 py-4 whitespace-nowrap font-medium">{sub.subjectName}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {sub.teacherId ? (
                                <span>{sub.teacherId.name} <span className="text-gray-400 text-sm">({sub.teacherId.email})</span></span>
                              ) : (
                                <span className="text-yellow-600">Not Assigned</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap space-x-2">
                              <button onClick={() => openEdit(sub)} className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                                Edit
                              </button>
                              <button onClick={() => handleDelete(sub._id)} className="text-red-600 hover:text-red-800 text-sm font-medium">
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No subjects assigned to this class yet.</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            /* Overview - all classes */
            <div className="space-y-6">
              {allSubjects.map((cls) => (
                <div key={cls.classId} className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-4 border-b bg-gray-50">
                    <h3 className="font-semibold text-lg">{cls.className} - {cls.section}</h3>
                    <p className="text-sm text-gray-500">Class Teacher: {cls.classTeacher}</p>
                  </div>
                  <div className="p-4">
                    {cls.subjects.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {cls.subjects.map((sub) => (
                          <div key={sub._id} className="border rounded-lg p-3 flex items-center gap-3">
                            <span className="text-2xl">📚</span>
                            <div>
                              <p className="font-medium">{sub.subjectName}</p>
                              <p className="text-sm text-gray-500">
                                {sub.teacherId ? sub.teacherId.name : 'No teacher assigned'}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">No subjects</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assign/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-semibold mb-4">
                {editingSubject ? 'Change Teacher' : 'Assign Subject'}
              </h2>
              <form onSubmit={handleAssign} className="space-y-4">
                {!editingSubject && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject Name</label>
                    <input
                      type="text"
                      required
                      value={formData.subjectName}
                      onChange={(e) => setFormData({...formData, subjectName: e.target.value})}
                      className="w-full border rounded-md px-3 py-2"
                      placeholder="e.g. Mathematics"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign Teacher</label>
                  <select
                    value={formData.teacherId}
                    onChange={(e) => setFormData({...formData, teacherId: e.target.value})}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="">-- Select Teacher --</option>
                    {teachers.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.name} ({t.employeeId || t.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); setEditingSubject(null); }}
                    className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
                    {editingSubject ? 'Update' : 'Assign'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrincipalSubjects;
